<template>
  <div
    class="rs-input"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="inputId"
    >{{ label }}<sup v-if="required || rules?.required">*</sup></label>
    <span v-if="helpTooltip">
      <RsIcon
        :id="`form-input-helper-tooltip-${id}`"
        icon="question-circle"
        class="tooltip-question-icon"
      />
      <RsTooltip
        :target="`form-input-helper-tooltip-${id}`"
        :title="helpTooltip"
        :position="helpTooltipPosition"
        :delay="{ show: 200 }"
      />
    </span>
    <slot name="tailLabel" />
    <Field
      v-slot="{ errors }"
      v-model="input"
      class="veeValidate"
      as="div"
      :name="validationName"
      :rules="rules"
      :validate-on-model-update="!isLazy"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>
          This field is managed by <strong>[Data Source]</strong>.<br>Changing
          this will be temporary.
        </p>
      </div>
      <div class="input-group input-wrap" role="group">
        <div v-if="prepend" class="input-group-prepend">
          <span class="input-group-text">{{ prepend }}</span>
        </div>
        <div v-if="icon && !imported" class="input-group-prepend">
          <span class="input-group-text">
            <RsIcon
              :icon="icon"
              weight="far"
            />
          </span>
        </div>
        <div
          v-if="imported"
          class="input-group-prepend"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'" />
          </span>
        </div>
        <!-- Ideally we use type='tel' but initially we want to use a custom type='phone' to not
        hijack type='tel' across the app and introduce this change slowly -->
        <RsPhoneInput
          v-if="type === 'phone'"
          :id="inputId"
          :value="input"
          :disabled="disabled"
          :placeholder="placeholder"
          :name="name"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          :is-international="isInternational"
          :readonly="readonly || (imported && isUnlocked == false)"
          :class="{ 'is-invalid': !!(errors[0] || hasCustomErrorState) }"
          @input="onPhoneInput"
          @keypress="$emit('keypress', $event)"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
          @valid="onPhoneInputValidityChange"
        />
        <!-- eslint-disable vuejs-accessibility/no-autofocus -->
        <input
          v-else
          :id="inputId"
          class="form-control"
          :class="{ 'is-invalid': !!(errors[0] || hasCustomErrorState) }"
          :value="input"
          :type="type"
          :disabled="disabled"
          :placeholder="placeholder"
          :maxlength="maxlength"
          :step="step"
          :min="min"
          :name="name"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          :readonly="readonly || (imported && isUnlocked == false)"
          @input="onNativeInput"
          @change="onNativeChange"
          @keypress="$emit('keypress', $event)"
          @keypress.enter="handleEnter"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
        />
        <!-- eslint-enable vuejs-accessibility/no-autofocus -->
        <div v-if="append" class="input-group-append">
          <span class="input-group-text">{{ append }}</span>
        </div>
      </div>
      <div
        v-if="errors.length || customErrorMessage || errorMessage"
        class="text--error"
      >
        {{ errors[0] || customErrorMessage || errorMessage }}
      </div>
      <div
        v-else-if="customWarningMessage"
        class="warning-message"
      >
        <RsIcon
          class="text--warning fas icon"
          icon="exclamation-triangle"
        />
        {{ customWarningMessage }}
      </div>
    </Field>
  </div>
</template>
<script lang="ts">
/* eslint-disable camelcase */
import { defineComponent, PropType } from 'vue';
import { Field, defineRule } from 'vee-validate';
import {
  required,
  email,
  numeric,
  max,
  min,
  max_value,
  min_value,
  digits,
  regex,
  alpha_num,
} from '@vee-validate/rules';
import type { TTooltipPosition } from '@/shared/types';
import { methods } from '@/mixins/validation-mixin';
import { isEmbedCode, embedCodeMsg } from '@/shared/helpers/is-embed-code';
import RsPhoneInput from '@/components/_global/RsPhoneInput.vue';
import i18n from '@/i18n';

defineRule('email', (value: unknown) => (email(value) ? true : i18n.t('validation.emailInvalid') as string));
defineRule('required', (value: unknown) => (required(value) ? true : i18n.t('validation.fieldRequired') as string));
defineRule('numeric', (value: unknown) => (numeric(value) ? true : i18n.t('validation.numericInvalid') as string));
defineRule('max', (value: unknown, params: [number]) => (max(value, params) ? true : i18n.t('validation.maxLength', { max: params[0] }) as string));
defineRule('min', (value: unknown, params: [number]) => (min(value, params) ? true : i18n.t('validation.minLength', { min: params[0] }) as string));
defineRule('max_value', (value: unknown, params: [number]) => (max_value(value, params) ? true : i18n.t('validation.maxValue', { max: params[0] }) as string));
defineRule('min_value', (value: unknown, params: [number]) => (min_value(value, params) ? true : i18n.t('validation.minValue', { min: params[0] }) as string));
defineRule('digits', (value: unknown, params: [number]) => (digits(value, params) ? true : i18n.t('validation.digits', { length: params[0] }) as string));
defineRule('regex', (value: unknown, params: [string]) => (regex(value, params) ? true : i18n.t('validation.regexInvalid') as string));
defineRule('url', (value: string) => (!value?.trim() || methods.isURL(value) || i18n.t('validation.urlInvalid') as string));
defineRule('numSpace', (value: string) => {
  const trimValue = value.replace(/\s/g, '');
  if (trimValue === '') return true;
  if (
    /^[0-9]*$/im.test(
      trimValue,
    ) === false
  ) {
    return i18n.t('validation.numericOnly') as string;
  }
  return true;
});
defineRule('embedCode', (value: string) => {
  const trimValue = value.replace(/\s/g, '');

  if (trimValue === '') return true;
  return isEmbedCode(trimValue) || i18n.t('validation.embedCodeInvalid') as string;
});
const phoneValidate = (value: string) => {
  const trimValue = value.replace(/\s/g, '');
  if (trimValue === '') return true;
  return /^[+]?[0-9]?[-\s.]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(trimValue);
};
defineRule('phone', (value: string) => phoneValidate(value) || i18n.t('validation.phoneInvalid') as string);
defineRule('phoneAlt', (value: string) => phoneValidate(value) || i18n.t('validation.phoneAltInvalid') as string);
defineRule('phoneIntl', (value: string) => {
  const trimValue = value.replace(/\s/g, '');
  if (trimValue === '') return true;
  return /^(?:\+1\d{10}|1\d{9,10}|[0,2-9]\d{9})$/.test(trimValue) || i18n.t('validation.phoneIntlInvalid') as string;
});
defineRule('alpha_num', (value: unknown) => (alpha_num(value, {}) ? true : i18n.t('validation.alphaNumOnly') as string));
defineRule('zipRegex', (zip: string) => {
  const zipRegex = new RegExp(
    /^\s*[0-9]{5}\s*$|^\s*[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]\s*$/,
    'i',
  );
  return zipRegex.test(String(zip).toLowerCase()) || i18n.t('validation.postalInvalid') as string;
});
defineRule('urlRegex', (value: string) => methods.isURL(value) || i18n.t('validation.urlRegexInvalid') as string);
defineRule('rent', (value: unknown) => (Number(value) >= 1 ? true : i18n.t('validation.rentMin') as string));

type TInputValue = string | number | null | undefined;

// Per-instance fallback for the `id` prop so `label[for]` still associates with the
// input when no id is passed (BootstrapVue previously auto-generated `__BVID__` ids).
let uid = 0;

export default defineComponent({
  name: 'RsFormInput',
  components: {
    RsPhoneInput,
    Field,
  },
  props: {
    rules: {
      type: Object,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    label: String,
    placeholder: String,
    readonly: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String as PropType<TTooltipPosition>,
      default: 'right',
    },
    htmlDecode: {
      type: Boolean,
      default: false,
    },
    icon: String,
    append: String,
    prepend: String,
    customErrorMessage: {
      type: String as () => string,
      required: false,
    },
    customWarningMessage: {
      type: String as () => string,
      required: false,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    /**
     *  Used to create a read-only input with the option to edit for imported fields.
     *
     */
    imported: {
      type: Boolean,
      default: false,
    },
    debounce: {
      type: Number,
      default: 0,
    },

    name: {
      type: String,
      default: 'input',
    },

    /**
     * success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop?: string) => ['success', 'warning', 'error', 'error-alt'].includes(prop ?? 'success'),
    },
    /**
     * text | number | email | password | search | url | tel | time | range | color
     *
     */
    type: {
      type: String,
      default: 'text',
      required: true,
      validator: (prop?: string) => [
        'text',
        'number',
        'email',
        'password',
        'search',
        'url',
        'tel',
        'time',
        'range',
        'color',
        'phone',
      ].includes(prop ?? 'text'),
    },
    maxlength: [String, Number],
    value: {
      type: [String, Number] as PropType<TInputValue>,
      default: '',
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: [String, Number] as PropType<TInputValue>,
      default: undefined,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    min: Number,
    step: Number,
    errorHighlight: {
      type: Boolean,
      default: false,
    },
    useNameForValidation: {
      type: Boolean,
    },
    /* Phone Number Input Only */
    isInternational: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['blur', 'enter', 'focus', 'input', 'keypress', 'valid', 'update:modelValue'],
  data() {
    uid += 1;
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      valid: null,
      types: [
        'text',
        'number',
        'email',
        'password',
        'search',
        'url',
        'tel',
        'time',
        'range',
        'color',
      ],
      input: '',
      errorMessage: '',
      fallbackId: `rs-input-${uid}`,
    };
  },
  computed: {
    /** The id used to pair the label with the input — the `id` prop, or a generated fallback. */
    inputId(): string {
      return this.id || this.fallbackId;
    },

    isLazy(): boolean {
      if (this.lazy && this.debounce > 0) {
        return false;
      }

      return this.lazy;
    },

    hasCustomErrorState(): boolean {
      return (!!this.customErrorMessage || this.errorHighlight);
    },

    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--success': this.variant === 'success',
        'rs-input--warning': this.variant === 'warning',
        'rs-input--error': this.variant === 'error',
        'rs-input--error-alt': this.variant === 'error-alt',
        'rs-input--search': this.type === 'search',
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
        'rs-input--compact': !!this.append || !!this.prepend,
      };
    },

    isHtmlDecode(): boolean {
      if (this.$rsGlobals.$isTranslateMode) {
        return true;
      }
      return this.htmlDecode;
    },
    validationName(): string {
      if (this.useNameForValidation && this.name) {
        return this.name;
      }

      // vee-validate v4 merges all `<Field>`s that share a `name` within the same
      // form into a single value — so two inputs with the same name silently bleed
      // one another's value (e.g. an unlabelled SEO-title input rendering the
      // meta-description's value). Because `name` defaults to 'input', every
      // unlabelled RsFormInput would otherwise collide; fall back to the
      // per-instance `fallbackId` so each field stays independent.
      if (this.label) return this.label;
      if (this.name && this.name !== 'input') return this.name;
      return this.fallbackId;
    },
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): TInputValue {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  watch: {
    boundValue: {
      immediate: true,
      handler() : void {
        this.input = this.isHtmlDecode ? this.$utils.decodeHtml(`${this.boundValue || ''}`, true) : `${this.boundValue || ''}`;
      },
    },
  },

  created() {
    (this as any)._debounceTimer = undefined as number | undefined;
  },

  beforeUnmount() {
    clearTimeout((this as any)._debounceTimer);
  },

  methods: {
    handleEnter(data: Event): void {
      this.$emit('enter', data);
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    onPhoneInput(val: string): void {
      this.input = val;
      const out = this.isHtmlDecode ? this.$utils.encodeHtml(val) : val;
      this.$emit('update:modelValue', out);
      this.$emit('input', out);
    },
    onNativeInput(e: Event): void {
      const val = (e.target as HTMLInputElement).value;
      this.input = val;
      if (!this.isLazy) this.scheduleEmit(val);
    },
    onNativeChange(e: Event): void {
      const val = (e.target as HTMLInputElement).value;
      this.input = val;
      if (this.isLazy) this.scheduleEmit(val);
    },
    scheduleEmit(val: string): void {
      clearTimeout((this as any)._debounceTimer);
      if (this.debounce > 0) {
        (this as any)._debounceTimer = window.setTimeout(() => this.emitInput(val), this.debounce);
      } else {
        this.emitInput(val);
      }
    },
    emitInput(val: string): void {
      const out = this.isHtmlDecode ? this.$utils.encodeHtml(val) : val;
      this.$emit('update:modelValue', out);
      this.$emit('input', out);
    },
    onPhoneInputValidityChange(value: boolean): void {
      this.$emit('valid', value);
      if (value === false && !this.customErrorMessage) { // === false Avoids null values
        this.errorMessage = this.$ts('validation.phoneInputInvalid');
      } else {
        this.errorMessage = '';
      }
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.rs-input {
  margin-bottom: 1rem;

  .iti {
    width: 100%;
  }

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
    font-size: $form-label-font-size;
    font-weight: $form-label-font-weight;
  }
  input {
    padding: $input-padding;
    width: 100%;
    height: 40px;
    background-color: $white;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    color: $input-color;
    font-size: $input-font-size;
    box-sizing: border-box;
    &:focus {
      box-shadow: 0 0 0 3px rgba($info, 0.1);
      border-color: $info;
    }
    @include placeholder {
      color: rgba($dark-gray, 0.4);
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  @each $name, $color in $status {
    &--#{$name} {
      input {
        border-color: $color;
        color: darken($color, 20%);
      }
    }
  }

  &--error-alt {
    input {
      border-color: $error;
      color: $error;
      background-color: rgba($error, 0.3);
    }
  }

  &--search {
    .input-group-prepend + input {
      border-left-width: 1px;
    }
  }
}
</style>
